import { defineStore } from 'pinia'
import {
  identifyOpenPanelUser,
  clearOpenPanelUser,
  openPanelChannel,
  op,
} from '@/lib/op'
import { authHeaders, setSessionToken } from '@/lib/sessionToken'

export type ActorPlatform = 'telegram' | 'web' | 'max' | 'guest' | string

export interface IdentityEntry {
  platform: string
  platformUserId: string
  platformChatId?: string | null
  username?: string | null
  firstName?: string | null
  lastName?: string | null
  firstSeenAt?: string
  lastSeenAt?: string
}

export interface UnifiedUser {
  userId: number
  state: 'guest' | 'active'
  name: string | null
  username?: string | null
  email: string | null
  identities: IdentityEntry[]
}

export type ActorContext = {
  platform: ActorPlatform
  platformUserId: string
  userName: string
}

type State = {
  actor: ActorContext | null
  user: UnifiedUser | null
  initialized: boolean
  authError: string | null
  unauthorized: boolean
  /** True while init() is resolving an auth method (keeps LoadingScreen up). */
  authPending: boolean
  /** Minted by the key-gated ?devtest login — never a real account. */
  devTest: boolean
  /** The current URL carries ?devtest. */
  devTestRequested: boolean
  /** Server confirmed a key hash is installed; show the password form. */
  devTestAvailable: boolean
  devTestChecking: boolean
  devTestError: string | null
}

async function postJson(url: string, body: unknown): Promise<{ ok: boolean; data?: unknown; error?: string }> {
  try {
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(body),
    })
    if (!res.ok) return { ok: false, error: `auth_failed_${res.status}` }
    return { ok: true, data: await res.json() }
  } catch (err: unknown) {
    return { ok: false, error: err instanceof Error ? err.message : 'Network error' }
  }
}

async function getJson(url: string): Promise<{ ok: boolean; data?: unknown; error?: string }> {
  try {
    const res = await fetch(url, {
      method: 'GET',
      headers: authHeaders({ Accept: 'application/json' }),
      credentials: 'include',
    })
    if (!res.ok) return { ok: false, error: `auth_failed_${res.status}` }
    return { ok: true, data: await res.json() }
  } catch (err: unknown) {
    return { ok: false, error: err instanceof Error ? err.message : 'Network error' }
  }
}

interface AuthResponse {
  success: boolean
  actor?: { platform: string; platformUserId: string; platformChatId?: number | string }
  user?: {
    userId: number
    state: 'guest' | 'active'
    name?: string | null
    username?: string | null
    email?: string | null
    identities?: IdentityEntry[]
  }
  /** Signed session token (bearer fallback for embedded iframes). */
  token?: string
  /** True only for a key-gated ?devtest session. */
  devtest?: boolean
}

/** Success payload of POST /api/auth/set-email (owned by webBridge authRoutes). */
interface SetEmailResponse {
  user?: {
    userId: number
    state: 'guest' | 'active'
    name?: string | null
    email?: string | null
    identities?: IdentityEntry[]
  }
  token?: string
  merged?: boolean
  promoted?: boolean
  mergedFromUserId?: number | null
}

function parseAuthResponse(data: unknown): { actor: ActorContext | null; user: UnifiedUser | null } {
  const d = data as AuthResponse | undefined
  if (!d?.success) return { actor: null, user: null }

  const u = d.user
  const a = d.actor
  const actor: ActorContext | null = a ? {
    platform: a.platform ?? 'unknown',
    platformUserId: String(a.platformUserId ?? ''),
    userName: u?.name ?? u?.username ?? '',
  } : null

  const user: UnifiedUser | null = u ? {
    userId: u.userId,
    state: u.state ?? 'active',
    name: u.name ?? null,
    username: u.username ?? null,
    email: u.email ?? null,
    identities: u.identities ?? [],
  } : null

  return { actor, user }
}

export const useIdentityStore = defineStore('identity', {
  state: (): State => ({
    actor: null,
    user: null,
    initialized: false,
    authError: null,
    unauthorized: false,
    authPending: false,
    devTest: false,
    devTestRequested: false,
    devTestAvailable: false,
    devTestChecking: false,
    devTestError: null,
  }),
  getters: {
    isAuthenticated: (s) => s.actor !== null,
    isGuest: (s) => s.user?.state === 'guest',
    isActive: (s) => s.user?.state === 'active',
    platform: (s) => s.actor?.platform ?? 'unknown',
    userId: (s) => s.user?.userId ?? 0,
    userName: (s) => {
      const name = s.user?.name?.trim()
      if (name) return name
      const username = s.user?.username?.trim()
      if (username) return `@${username}`
      return s.actor?.userName?.trim() || ''
    },
    userEmail: (s) => s.user?.email ?? null,
    coreUserId: (s) => s.user?.userId ?? 0,
    identities: (s) => s.user?.identities ?? [],
    isDevTest: (s) => s.devTest,
  },
  actions: {
    async init() {
      if (this.initialized || this.authPending) return
      this.authPending = true
      this.unauthorized = false
      this.authError = null
      this.devTestAvailable = false
      this.devTestError = null
      this.devTestRequested = typeof window !== 'undefined'
        && new URLSearchParams(window.location.search).has('devtest')

      const withAuth = (result: { ok: boolean; data?: unknown; error?: string }) => {
        if (result.ok) {
          const { actor, user } = parseAuthResponse(result.data)
          if (actor) {
            this.actor = actor
            this.user = user
            const data = result.data as AuthResponse | undefined
            this.devTest = data?.devtest === true
            // Bearer fallback for embedded clients (third-party iframes drop cookies)
            if (typeof data?.token === 'string' && data.token.length > 0) setSessionToken(data.token)
            // Unify the browser session with the backend-tracked OpenPanel profile.
            identifyOpenPanelUser(user, actor.platformUserId, openPanelChannel(actor.platform))
            return true
          }
        }
        this.authError = result.error ?? null
        return false
      }

      try {
        // Standalone UI demo (dev only): `#/assistant?demo=1` renders fixtures
        // with no webBridge and no auth. Keeps the new chat inspectable on a
        // machine where the NATS/webBridge stack is not running.
        if (import.meta.env.DEV && typeof window !== 'undefined' && window.location.hash.includes('demo=1')) {
          this.actor = { platform: 'web', platformUserId: 'dev-demo', userName: 'Demo User' }
          this.user = { userId: 999001, state: 'active', name: 'Demo User', username: 'demo', email: null, identities: [] }
          return
        }

        // 0. Explicit operator access: ?devtest.
        //    Never fall through to Telegram/iframe with this param — a real
        //    cookie must not silently turn into a "test" session. Only a
        //    devtest-flagged session (cookie or bearer) is accepted; otherwise
        //    the key form appears when the server has a hash installed.
        if (this.devTestRequested && typeof window !== 'undefined') {
          const sessionResult = await getJson('/api/auth/session')
          const sessionData = sessionResult.ok ? sessionResult.data as AuthResponse : undefined
          if (sessionData?.devtest === true && withAuth(sessionResult)) return

          const status = await getJson('/api/devtest/status')
          const statusData: unknown = status.data
          const enabled = status.ok
            && typeof statusData === 'object'
            && statusData !== null
            && 'enabled' in statusData
            && statusData.enabled === true
          if (enabled) {
            this.devTestAvailable = true
            op?.ready()
            return
          }
        }

        // 1. Telegram Mini App
        if (typeof window !== 'undefined') {
          const tg = window.Telegram?.WebApp
          if (tg?.initData) {
            try { tg.ready(); tg.expand?.() } catch { /* ok */ }
            const result = await postJson('/api/auth/telegram', { initData: tg.initData })
            if (withAuth(result)) return
          }
        }

        // 2. Max Mini App
        if (typeof window !== 'undefined') {
          const maxApp = window.WebApp
          if (maxApp?.initData) {
            const result = await postJson('/api/auth/max', { initData: maxApp.initData })
            if (withAuth(result)) return
          }
        }

        // 3. URL token auth (iframe embed)
        if (typeof window !== 'undefined') {
          const urlParams = new URLSearchParams(window.location.search)
          const token = urlParams.get('token')
          if (token) {
            const result = await postJson('/api/auth/iframe', { token })
            if (withAuth(result)) return
          }
        }

        // 4. Dev auth fallback (local development only).
        //    webBridge exposes POST /api/auth/dev when
        //    WEB_BRIDGE_ENABLE_DEV_AUTH=1, minting a session for any
        //    userId. Returns 404 in production builds (flag off). This
        //    step lets you open the SPA on localhost and see real data
        //    without Telegram/Max/iframe token plumbing.
        if (typeof window !== 'undefined' && import.meta.env.DEV) {
          const devUserId = 'dev-local'
          const result = await postJson('/api/auth/dev', {
            userId: devUserId,
            platform: 'web',
            name: 'Dev User',
          })
          if (withAuth(result)) return
        }

        // 5. Nothing worked — truly unauthorized (guest auth is disabled)
        this.actor = null
        this.user = null
        this.unauthorized = true
        // Flush any queued screen views anonymously (no profile to unify with).
        op?.ready()
        console.warn('[identity] No auth available — showing gate')
      } catch (err) {
        console.error('[identity] init failed', err)
        this.authError = 'init_failed'
        this.unauthorized = true
      } finally {
        this.authPending = false
        this.initialized = true
      }
    },

    /**
     * Exchange the operator key for a devtest session. The key is sent once
     * over HTTPS and never stored in the browser; only the signed session is.
     */
    async loginDevTest(key: string): Promise<boolean> {
      if (this.devTestChecking) return false
      this.devTestChecking = true
      this.devTestError = null
      try {
        const result = await postJson('/api/devtest/login', { key })
        if (result.ok) {
          const { actor, user } = parseAuthResponse(result.data)
          const data = result.data as AuthResponse | undefined
          if (actor && data?.devtest === true) {
            this.actor = actor
            this.user = user
            this.devTest = true
            this.devTestAvailable = false
            this.unauthorized = false
            this.authError = null
            if (typeof data.token === 'string' && data.token.length > 0) setSessionToken(data.token)
            identifyOpenPanelUser(user, actor.platformUserId, openPanelChannel(actor.platform))
            return true
          }
        }
        this.devTestError = result.error === 'auth_failed_429'
          ? 'too_many_attempts'
          : result.error === 'auth_failed_500'
            ? 'login_failed'
            : 'invalid_key'
        return false
      } finally {
        this.devTestChecking = false
      }
    },

    /** Set email on the current user. Handles guest→active promotion and account merging. */
    async setEmail(email: string): Promise<{ success: boolean; merged: boolean }> {
      const result = await postJson('/api/auth/set-email', { email })
      if (result.ok) {
        // Shape owned by webBridge authRoutes (/api/auth/set-email success payload).
        const d = (result.data ?? {}) as SetEmailResponse
        // Fresh signed session comes back with the promotion/merge result.
        if (typeof d.token === 'string' && d.token.length > 0) setSessionToken(d.token)
        if (d.user) {
          this.user = {
            userId: d.user.userId,
            state: d.user.state ?? 'active',
            name: d.user.name ?? this.user?.name ?? null,
            email: d.user.email ?? email,
            identities: d.user.identities ?? this.user?.identities ?? [],
          }
          // Guest→active promotion / merge may change the unified userId —
          // re-identify so the browser session follows the new profile.
          if (this.actor) {
            identifyOpenPanelUser(this.user, this.actor.platformUserId, openPanelChannel(this.actor.platform))
          }
        }
        return { success: true, merged: d?.merged ?? false }
      }
      return { success: false, merged: false }
    },

    expire() {
      this.actor = null
      this.user = null
      this.initialized = false
      this.authError = 'session_expired'
      this.unauthorized = false
      this.authPending = false
      this.devTest = false
      this.devTestRequested = false
      this.devTestAvailable = false
      this.devTestChecking = false
      this.devTestError = null
      setSessionToken(null)
      clearOpenPanelUser()
    },
  },
})
